import { MouseEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Button from '@material-ui/core/Button';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { t } from 'src/lib/language';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { NotificationContact, NotificationModel } from 'src/models/NotificationModel';
import { saveNotificationAction } from 'src/redux/actions/notificationActions';
import NotificationContactValidator from 'src/validations/NotificationContactValidator';
import BottomRow from 'src/components/UI/BottomRow';
import Form from 'src/components/UI/Form';

interface Props {
  notification: NotificationModel;
  contact: NotificationContact;
  onClose?: () => void;
}

const EditNotificationContactForm = (props: Props) => {
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const { notification, contact, onClose } = props;

  const processing = useSelector(
    (state: Store) => state.formStatus[FORM.save_notification] === FORM_STATUS.processing,
  );

  const [state, setState] = useState({
    email: contact.email ?? '',
    phone: contact.phone ?? '',
  });
  const form = FORM.save_notification;

  useEffect(() => {
    dispatch(setError({ [form]: {} }));
  }, []);

  const onChange = (value: any, field: string) => {
    setState({
      ...state,
      [field]: value,
    });
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [form]: {} }));
    try {
      NotificationContactValidator(lang).validateSync(state, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'forms.organisation.validation_errors',
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [form]: errorBag }));
      return false;
    }
    const contacts = notification.contacts.map((item) => (
      item.id === contact.id
        ? { ...item, email: state.email || null, phone: state.phone || null }
        : item
    ));
    dispatch(saveNotificationAction(notification, contacts, notification.type));
    return true;
  };

  return (
    <Form>
      <FormTextField
        form={form}
        label={t(lang, 'forms.organisation.email')}
        name="email"
        onChange={onChange}
        value={state.email}
        disabled={processing} />
      <FormTextField
        form={form}
        label={t(lang, 'forms.organisation.phone')}
        name="phone"
        onChange={onChange}
        value={state.phone}
        disabled={processing} />
      <BottomRow>
        <Button
          variant="contained"
          color="primary"
          style={{ marginTop: '12px' }}
          onClick={handleSubmit}
          key="submit-button"
          disabled={processing}>
          {t(lang, 'misc.save')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          style={{ marginTop: '12px' }}
          onClick={handleClose}
          key={`submit-button-contact-${contact.id}`}>
          {t(lang, 'misc.back_to_list')}
        </Button>
      </BottomRow>
    </Form>
  );
};

export default EditNotificationContactForm;
